import type { Universe } from '@/types/universe';

interface HeroProps {
  universe: Universe;
  universes: Universe[];
  activeUniverse: number;
  onUniverseChange: (idx: number) => void;
  heroVisible: boolean;
}

export default function Hero({
  universe,
  universes,
  activeUniverse,
  onUniverseChange,
  heroVisible,
}: HeroProps) {
  return (
    <section id="hero" className="relative min-h-screen flex flex-col overflow-hidden">
      {/* Background */}
      <div className="absolute inset-0 z-0">
        <img
          src={universe.heroBg}
          alt={`Univers ${universe.label}`}
          className={`w-full h-full object-cover transition-opacity duration-700 ${heroVisible ? 'opacity-100' : 'opacity-0'}`} />
        {/* Assombrissement vertical : sombre en bas (où vit le texte), plus léger en haut */}
        <div className="absolute inset-0 bg-gradient-to-t from-black/85 via-black/50 to-black/40" />
        {/* Dégradé latéral : garantit la lisibilité de la colonne de texte alignée à gauche */}
        <div className="absolute inset-0 bg-gradient-to-r from-black/70 via-black/25 to-transparent" />
      </div>


      {/* Bloc bas : contenu + stats, poussés vers le bas via mt-auto */}
      <div className="relative z-10 mt-auto pb-31">
        {/* Hero Content */}
        <div className="px-6 md:px-16 lg:px-24 max-w-5xl">
          <p className="flex items-center text-accent text-[11px] font-semibold uppercase tracking-widest mb-6">
            <span className="accent-line" />
            {universe.heroSubtitle}
          </p>
          <h1
            className={`font-display text-white leading-tight mb-8 transition-opacity duration-500 ${heroVisible ? 'animate-fade-in-up' : 'opacity-0'}`}
            style={{ fontSize: 'clamp(44px, 6.5vw, 88px)', fontFamily: "'DM Serif Display', Georgia, serif" }}>
            {universe.heroTitle}{' '}
            <em className="italic">{universe.heroTitleItalic}</em>{' '}
            {universe.heroTitleEnd}
          </h1>
          <div className={`transition-opacity duration-500 ${heroVisible ? 'animate-fade-in-up animate-delay-200' : 'opacity-0'}`}>
            <p className="text-white/80 text-base md:text-lg mb-8 max-w-xl">{universe.heroDesc}</p>
            <div className="flex flex-wrap gap-4">
              <a
                href="#pricing"
                className="bg-accent text-primary font-semibold text-[11px] uppercase tracking-widest px-8 py-4 rounded-full hover:bg-accent-light transition-colors duration-200">
                Voir les offres
              </a>
              <a
                href="#how-it-works"
                className="border border-white/30 text-white font-semibold text-[11px] uppercase tracking-widest px-8 py-4 rounded-full hover:bg-white/10 transition-colors duration-200">
                Comment ça marche
              </a>
            </div>
          </div>
        </div>

        {/* Stats */}
        <div className="mt-12 px-6 md:px-16 lg:px-24">
          <div className="w-full h-px bg-white/20 mb-8" />
          <div className="flex flex-wrap gap-8 md:gap-16">
            {universe.stats.map((stat, i) =>
              <div key={i}>
                <p className="text-white font-bold text-2xl md:text-3xl" style={{ fontFamily: "'DM Serif Display', Georgia, serif" }}>{stat.value}</p>
                <p className="text-white/60 text-xs uppercase tracking-widest mt-1">{stat.label}</p>
              </div>
            )}
          </div>
        </div>
      </div>
    </section>
  );
}